import React, { useEffect, useState, useMemo } from "react";
import { Order, OrderDress } from "../../api/OrdersAPI";
import CONFIG from "../../config/config";
import ProductTypesAPI from "../../api/ProductTypesAPI";
import ShopClientsAPI, { ShopClient } from "../../api/ShopClientsAPI";
import SelectInput from "../SelectInput/SelectInput";
import TextArea from "../TextArea/TextArea";
import { orderStatusToTranslationKeys } from "../../shared/orderStatusToTranslationKey";
import { useSelector } from "react-redux";
import { Settings } from "../../store/reducers/AccountSlice";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { paymentStatusToTranslationKey, productTypeToIcon, productTypeToTranslationKey } from "../../shared/paymentStatusToTranslationKey";
import 'react-datepicker/dist/react-datepicker.css';
import FileUploadWrapper from "../FileUploadWrapper/FileUploadWrapper";
import SectionTitle from "../SectionTitle/SectionTitle";
import OrderTrackingForm from "../OrderTrackingForm/OrderTrackingForm";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import { toastService } from "../../services/ToastService";
import { format } from "date-fns";
import FileList from "../FileList/FileList";
import SmallCheckBox from "../SmallCheckBox/SmallCheckBox";
import { isValidText } from "../../shared/validation";
import useFeatureFlag from "../../hooks/usePermissionFlag";
import CustomerInput from "../CustomerInput/CustomerInput";
import { ItemAutocomplete } from "../ItemAutocomplete/ItemAutocomplete";
import { ItemVariant } from "../../api/CollectionItemVariantsAPI";
import SelectInputV2 from "../SelectInputV2/SelectInputV2";
import Notice from "../Notice/Notice";
import InputShortText from "../InputShortText/InputShortText";
import { AdminRestricted } from "../AdminRestricted/AdminRestricted";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import FilesAPI from "../../api/FilesAPI";
import { extractFileName } from "../PrivateResourceLink/PrivateResourceLink";
import { ButtonV2 } from "../ButtonV2/ButtonV2";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import CopyText from "../CopyText/CopyText";
import { PaymentStatus } from "../../types/payment-statuses";
import TextOptionalActionButton from "../TextOptionalActionButton/TextOptionalActionButton";
import PaymentPlanManager from "../PaymentPlanManager/PaymentPlanManager";
import OrdersAPI from "../../api/OrdersAPI";
import { Modal } from "../Modal/Modal";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import CollectionsAPI, { Item, Collection } from "../../api/CollectionsAPI";
import PriceField from "../PriceField/PriceField";

type Props = {
  order: Order | null,
  isExternal?: boolean,
  onOrderChange?: (value: Order) => void,
  isDisabled?: boolean,
  actionButton?: JSX.Element,
  onValidationChange?: (isValid: boolean) => void
};

function OrderForm({ order, isExternal = false, onOrderChange = () => null, isDisabled = false, actionButton, onValidationChange }: Props) {

  const { t } = useTranslation();
  const location = useLocation();
  const settings: Settings = useSelector((state: any) => state.settings.value);

  const { hasAccess } = useFeatureFlag(['update-orders']);
  const [showTracking, setShowTracking] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [types, setTypes] = useState<string[]>([]);
  const [shops, setShops] = useState<ShopClient[]>([]);
  const [variants, setVariants] = useState<ItemVariant[]>([]);
  const [showVariants, setShowVariants] = useState(false);
  const [chosenItemIsExternal, setChosenItemIsExternal] = useState(false);
  const [showPaymentPlanManager, setShowPaymentPlanManager] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [newItem, setNewItem] = useState<Item | null>(null);
  const [showCatalogueSelectionModal, setShowCatalogueSelectionModal] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);

  const [, setIsValid] = useState(false);

  const getAvailableOrderStatuses = useMemo(() => {
    const allOrderStatuses = CONFIG.ORDERS.STATUS;
    
    // Use configured statuses if available, otherwise fall back to all statuses
    const baseStatuses = (settings?.isConfigurationLoaded && 
                         settings?.allowedOrderStatuses && 
                         settings.allowedOrderStatuses.length > 0)
      ? settings.allowedOrderStatuses
      : allOrderStatuses;
    
    // If current order has a status not in the base list, include it
    const currentOrderStatus = order?.status;
    const shouldIncludeCurrentStatus = currentOrderStatus && 
                                     baseStatuses &&
                                     !baseStatuses.includes(currentOrderStatus);
    
    const availableStatuses = shouldIncludeCurrentStatus && baseStatuses
      ? [...baseStatuses, currentOrderStatus]
      : baseStatuses || [];
    
    // Remove duplicates and create select options
    const uniqueStatuses = Array.from(new Set(availableStatuses));
    
    return uniqueStatuses
      .map(status => ({ 
        value: status, 
        readableValue: t(orderStatusToTranslationKeys(status)) 
      }))
      .sort((a, b) => a.readableValue.localeCompare(b.readableValue));
  }, [settings?.isConfigurationLoaded, settings?.allowedOrderStatuses, order?.status, t]);

  const statusSelectOptions = getAvailableOrderStatuses;

  useEffect(() => {
    const valid = !!order?.dress && !!order?.clientName;
    setIsValid(valid);
    onValidationChange?.(valid);
  }, [order?.dress, order?.clientName, onValidationChange]);

  useEffect(() => {
    const valid = !!order?.dress && !!order?.clientName;
    setIsValid(valid);
    onValidationChange?.(valid);
  }, [order?.dress, order?.clientName, chosenItemIsExternal, onValidationChange]);

  useEffect(() => {
    setShowTracking(!!order && !!order.id);
    const load = async () => {
      const shopsResp = await ShopClientsAPI.get();
      const typesResp = await ProductTypesAPI.get();
      setTypes(typesResp.data);
      setShops(shopsResp.data.map(s => s));
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyOrderLink = () => {
    if (!order?.id) return;
    const basePath = location.pathname.includes('/production-orders') ? '/production-orders' : '/orders';
    return `${window.location.origin}${basePath}?orderId=${order.id}`;
  };

  const removeLink = (url: string) => {
    const updatedAttachments = order?.attachmentUrls?.filter((attachmentUrl) => attachmentUrl !== url);
    onOrderChange({ ...order, attachmentUrls: updatedAttachments } as Order);
  }

  const removeFile = (fileName: string) => {
    const updatedFiles = order?.files?.filter((file) => file.name !== fileName);
    onOrderChange({ ...order, files: updatedFiles } as Order);
  }

  const renameFile = (oldFile: File, newName: string, index: number) => {
    if (!order?.files) return;
    const updatedFiles = [...order.files];
    updatedFiles[index] = new File([oldFile], newName, { type: oldFile.type });
    onOrderChange({ ...order, files: updatedFiles } as Order);
  }

  const processFile = async (files: File[]) => {
    try {
      const newFiles = [...order?.files || [], ...files];
      onOrderChange({ ...order, files: newFiles } as Order);
    } catch (error) {
      toastService.showToast(t("toast.errors.add_file"), 'error');
    }
  };

  const handleCreateItem = () => {
    setNewItem({
      name: '',
      featuredPictureUrl: '',
      type: 'dresses',
      placeOrder: 0,
      price: 0,
      currency: 'EUR',
      description: '',
      media: [],
      variations: []
    });
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!newItem || !newItem.name) {
      toastService.showToast(t("errors.too_short") || "Item name is required", 'error');
      return;
    }

    if (!newItem.price || newItem.price <= 0) {
      toastService.showToast(t("sales.form.item.price_required") || "Price is required", 'error');
      return;
    }

    try {
      // Fetch collections to see if any exist
      const collectionsResponse = await CollectionsAPI.getCollections();
      const availableCollections = collectionsResponse.data || [];

      setCollections(availableCollections);
      setShowItemModal(false);

      if (availableCollections.length > 0) {
        // Show catalogue selection modal
        setShowCatalogueSelectionModal(true);
      } else {
        // No catalogues exist, create default one and add item
        const defaultCollection: Collection = {
          name: t("sales.form.default_collection_name") || "Catalog",
          shortName: "catalog",
          type: "dresses",
          types: ["dresses"],
          views: 0,
          description: t("sales.form.default_collection_description") || "Catalog created automatically",
          featured: false,
          public: false,
          lastUpdateTime: null,
          items: [],
          shareableByLink: true,
          pricesArePublic: false
        };

        const createdCollection = await CollectionsAPI.add(defaultCollection);
        const collectionId = createdCollection.data.id!;
        
        // Add item to the default collection
        const createdItem = await CollectionsAPI.addItem(collectionId, newItem);
        
        toastService.showToast(
          t("sales.form.item.created_with_default_collection") || 
          "Item created and added to catalog. You can configure the catalog in the Catalogues section.",
          "success"
        );
        
        // Get the created item with its ID from the response
        const itemWithId = createdItem.data || { ...newItem };
        
        // Trigger the item selection handler to simulate selecting the item
        const newDress: OrderDress = {
          name: itemWithId.name,
          dressPlanId: itemWithId.id?.toString() || '',
          type: "CATALOG_ITEM"
        };
        const dressType = itemWithId.type || 'dresses';
        onOrderChange({ 
          ...order, 
          dress: newDress, 
          type: dressType 
        } as Order);
        setVariants(itemWithId.variations || []);
        
        setNewItem(null);
      }
    } catch (error) {
      toastService.showToast(t("sales.form.item.create_error") || "Failed to create item", 'error');
    }
  };

  const handleAddItemToCollection = async () => {
    if (!selectedCollectionId || !newItem) {
      toastService.showToast(t("sales.form.item.select_collection") || "Please select a collection", 'error');
      return;
    }

    try {
      const createdItem = await CollectionsAPI.addItem(selectedCollectionId, newItem);
      toastService.showToast(t("sales.form.item.created") || "Item created successfully", "success");
      
      // Get the created item with its ID from the response
      const itemWithId = createdItem.data || { ...newItem };
      
      // Trigger the item selection handler to simulate selecting the item
      const newDress: OrderDress = {
        name: itemWithId.name,
        dressPlanId: itemWithId.id?.toString() || '',
        type: "CATALOG_ITEM"
      };
      const dressType = itemWithId.type || 'dresses';
      onOrderChange({ 
        ...order, 
        dress: newDress, 
        type: dressType 
      } as Order);
      setVariants(itemWithId.variations || []);
      
      setShowCatalogueSelectionModal(false);
      setNewItem(null);
      setSelectedCollectionId(null);
    } catch (error) {
      toastService.showToast(t("sales.form.item.create_error") || "Failed to create item", 'error');
    }
  };

  const downloadAllFiles = async () => {
    if (!order?.attachmentUrls || order.attachmentUrls.length === 0) return;
    
    setIsDownloading(true);
    
    try {
      const zip = new JSZip();
      let successCount = 0;
      
      const addFileToZip = async (fileUrl: string) => {
        try {
          const fileName = extractFileName(fileUrl);
          const response = await FilesAPI.getUrl(fileName, 'orders');
          const url = response.data.url;
          
          const fileResponse = await fetch(url);
          const fileBlob = await fileResponse.blob();
          
          zip.file(fileName, fileBlob);
          return true;
        } catch (error) {
          console.error(`Error adding file to zip: ${fileUrl}`, error);
          return false;
        }
      };
      
      for (const fileUrl of order.attachmentUrls) {
        const success = await addFileToZip(fileUrl);
        if (success) successCount++;
      }
      
      const zipBlob = await zip.generateAsync({type: 'blob'});
      
      saveAs(zipBlob, `order-${order.id || 'files'}.zip`);
      
      if (successCount === order.attachmentUrls.length) {
        toastService.showToast(t("global.zip_created_successfully"));
      } else {
        toastService.showToast(
          t("global.some_files_not_added", { count: successCount, total: order.attachmentUrls.length }),
          "error"
        );
      }
    } catch (error) {
      toastService.showToast(t("global.zip_creation_failed"), "error");
      console.error("Error creating zip file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePaymentPlanManagement = () => {
    setShowPaymentPlanManager(true);
  };

  const handlePaymentPlanUpdated = async () => {
    if (order?.id) {
      try {
        const response = await OrdersAPI.getInternalOrderById(order.id.toString());
        const updatedOrder = response.data as Order;
        onOrderChange({
          ...(order as Order),
          paymentStatus: updatedOrder.paymentStatus,
        } as Order);
      } catch (error) {
        console.error('Failed to refresh order data after payment plan update:', error);
        toastService.showToast(t("global.error", "Failed to refresh order after payment plan update."), "error");
      }
    }
  };

  const disabledActionButton = actionButton
    ? React.cloneElement(
      actionButton,
      {},
      React.Children.map(actionButton.props.children, (child) => {
        return child ? React.cloneElement(child, { isDisabled: isDisabled }) : null;
      })
    )
    : null;

  return (
    <>
      <div className="container flex flex-wrap mb-5 space-x-0 sm:space-x-5 space-y-5 sm:space-y-0">
        <div className="flex-1 space-y-5">
          {order?.id && (
            <div className="flex items-center mb-2 space-x-2">
              {copyOrderLink() && <CopyText text={copyOrderLink()!} />}
              <span className="text-sm opacity-80">
                #{order?.id}
              </span>
            </div>
          )}
          <SectionTitle text={t("orders.orders_basket.add_form.general_data")} />
          <CustomerInput
            onCustomerSelect={c => 
              onOrderChange({ ...order, customer: c, clientName: `${c?.name}${c?.surname ? ` ${c?.surname}` : ''}`} as Order)
            }
            label={"orders.orders_basket.add_form.name"}
            onChange={(e: any) => onOrderChange({ ...order, clientName: e.currentTarget.value } as Order)}
            errorMessage={"errors.too_short"}
            defaultValue={order?.clientName || ''}
            isDisabled={isDisabled || !hasAccess}
            helperText={t("orders.orders_basket.add_form.name.helper", "Enter the customer's full name. You can search for existing customers or create a new one.") || ""}
          />
          <ItemAutocomplete
            onChange={e => {
              setVariants(e.variations || []);
              const { id, type, ...rest } = e;
              const newDress = { ...rest, dressPlanId: id, type: "CATALOG_ITEM" };
              const dressType = type || 'dresses';
              onOrderChange({ 
                ...order, 
                dress: newDress, 
                type: dressType 
              } as Order);
              setChosenItemIsExternal(e.isExternal);
              setShowVariants(false);
            }}
            filterDress={(item) => {
              if (isExternal && !order?.id) {
                return item.isExternal === true;
              }
              return true;
            }}
            label={"orders.orders_basket.add_form.dress"}
            defaultValue={(order?.dress as OrderDress)?.name}
            isDisabled={isDisabled || !hasAccess}
            errorMessage={t("orders.orders_basket.add_form.dress.error")!}
          />
          {!isExternal && (
            <AdminRestricted>
              <div className="mt-2">
                <TextOptionalActionButton
                  text={t("sales.form.add_model") || "Add a new model"}
                  onClick={handleCreateItem}
                  disabled={isDisabled || !hasAccess}
                  actionName="Add Model"
                />
              </div>
            </AdminRestricted>
          )}
          { chosenItemIsExternal && 
            <>
              <Notice 
                pillText={t("global.notice")} 
                tinyText={t("orders.orders_basket.add_form.dress.external_warning", "This is an external item. Some fields will be hidden as they are not applicable for external orders.") || ""}
              />
            </>
          }
          {variants && variants.length > 0 && (
            <>
              <div className="flex items-center space-x-2">
                <SmallCheckBox
                  onChange={(event: any) => setShowVariants(Boolean(event.currentTarget.checked))}
                  isDisabled={isDisabled || !hasAccess}
                  helperText={t("orders.orders_basket.add_form.dress.variants.helper", "Check this box to see and select from available variants of this item.") || ""}
                />
                <p>{t("orders.orders_basket.add_form.dress.see_variants")}.</p>
              </div>

              {showVariants && (
                <>
                  <SelectInput values={variants.map(v => { return { value: v.name, readableValue: v.name};} )}
                    onChange={e => {
                      const variantId = variants.filter(v => v.name === e.target.value).map(v => v.id)[0];
                      const itemVariant = { dressPlanId: String(variantId), type: "CATALOG_ITEM_VARIANT", name: e.target.value };
                      onOrderChange({ ...order, dress: itemVariant } as Order);
                    }}
                    label={"orders.orders_basket.add_form.dress.variant"}
                    isDisabled={isDisabled || !hasAccess}
                    helperText={t("orders.orders_basket.add_form.dress.variant.helper", "Select a specific variant of the chosen item.") || ""}
                  />
                </>
              )}
            </>
          )}
          {order?.dress && (
          <>
            {!(isExternal && !order?.id) && (
            <div className="flex flex-wrap">
              <InputShortText
                label={"orders.orders_basket.add_form.invoice_number"}
                validationFn={(value) => !value || isValidText(value, 0, 50)}
                onChange={(e) => onOrderChange({ ...order, invoiceNumber: e.target.value } as Order)}
                errorMessage={"errors.too_long_short"}
                defaultValue={order?.invoiceNumber || ''}
                isDisabled={isDisabled || !hasAccess}
                placeholder={"orders.orders_basket.add_form.invoice_number.placeholder"}
                helperText={t("orders.orders_basket.add_form.invoice_number.helper", "Optional: Enter the invoice number for this order.") || "Optional: Enter the invoice number for this order."}
              />
            </div>
            )}
            <div className="flex flex-wrap space-x-5">
              <CustomDatePicker
                isDisabled={isDisabled || !hasAccess}
                label="orders.orders_basket.add_form.delivery_time"
                value={order?.deliveryDate}
                onChange={c => onOrderChange({ ...order, deliveryDate: format(c, 'yyyy-MM-dd') } as Order)}
                helperText={t("orders.orders_basket.add_form.delivery_time.helper", "Set the latest date when this order needs to be sent.") || ""}
              />
              <CustomDatePicker
                isDisabled={isDisabled || !hasAccess}
                label="orders.orders_basket.add_form.wedding_date"
                value={order?.weddingDate}
                onChange={c => onOrderChange({ ...order, weddingDate: format(c, 'yyyy-MM-dd') } as Order)}
                helperText={t("orders.orders_basket.add_form.wedding_date.helper", "Set the date of the wedding.") || ""}
              />
            </div>
            {!chosenItemIsExternal && !(isExternal && !order?.id) && (
              <div className="flex flex-wrap space-x-5">
                <SelectInput
                  values={shops.map(e => { return { value: String(e.id!), readableValue: e.name } })}
                  onChange={e => 
                    onOrderChange(
                      { ...order, 
                        linkedShopId: Number(e.currentTarget.value), 
                        shopName: shops.filter(s => String(s.id) === e.currentTarget.value)[0].name 
                      } as Order)
                  }
                  label={"orders.orders_basket.add_form.salon"}
                  defaultValue={String(order?.linkedShopId)}
                  isDisabled={isDisabled || !hasAccess}
                  helperText={t("orders.orders_basket.add_form.salon.helper", "Select the salon where this order will be delivered.") || ""}
                />
                <SelectInputV2
                  values={types
                    .map(e => ({ value: e, readableValue: t(productTypeToTranslationKey(e)), icon: <>{productTypeToIcon(e)}</> }))
                    .sort((a, b) => a.readableValue.localeCompare(b.readableValue))
                  }
                  onChange={e => onOrderChange({ ...order, type: e } as Order)}
                  label={"orders.orders_basket.add_form.type"}
                  defaultValue={order?.type}
                  isDisabled={isDisabled || !hasAccess}
                  helperText={t("orders.orders_basket.add_form.type.helper", "Select the type of product for this order.") || ""}
                />
              </div>
            )}
            {!chosenItemIsExternal && !(isExternal && !order?.id) && (
              <div className="flex flex-wrap space-x-5">
                <div className="flex-1">
                  <SelectInput values={CONFIG.ORDERS.PAYMENT_STATUS
                    .map(e => ({ value: e, readableValue: t(paymentStatusToTranslationKey(e)) }))
                    .sort((a, b) => a.readableValue.localeCompare(b.readableValue))
                  }
                    onChange={e => onOrderChange({ ...order, paymentStatus: e.target.value as PaymentStatus } as Order)}
                    label={"orders.orders_basket.add_form.payment_status"}
                    defaultValue={order?.paymentStatus}
                    isDisabled={isDisabled || !hasAccess}
                    helperText={t("orders.orders_basket.add_form.payment_status.helper", "Set the current payment status of this order.") || ""}
                  />
                  {order?.id && (
                    <div className="mt-2">
                      <TextOptionalActionButton
                        text={t("orders.payment_plan.see_payment_plan", "See payment plan")}
                        onClick={handlePaymentPlanManagement}
                        disabled={isDisabled || !hasAccess}
                        actionName="See Payment Plan"
                        icon={<ExternalLink className="h-4 w-4" />}
                        variant="blue"
                      />
                    </div>
                  )}
                </div>
                <SelectInput values={statusSelectOptions}
                  onChange={e => onOrderChange({ ...order, status: e.target.value } as Order)}
                  label={"orders.orders_basket.add_form.status"}
                  defaultValue={order?.status}
                  isDisabled={isDisabled || !hasAccess}
                  helperText={t("orders.orders_basket.add_form.status.helper", "Set the current status of this order.") || ""}
                />
              </div>
            )}
            <TextArea
              label={"orders.orders_basket.add_form.notes"}
              validationFn={(value) => !value || (!!value &&  isValidText(value, 0, 1000))}
              onChange={e => onOrderChange({ ...order, notes: e.currentTarget.value } as Order)}
              errorMessage={"errors.too_short"}
              defaultValue={order?.notes || ''}
              isDisabled={isDisabled || !hasAccess}
              maxLength={1000}
              helperText={t("orders.orders_basket.add_form.notes.helper", "Add any additional notes or special requirements for this order (e.g. special instructions for the delivery, adjustements, measures from the customer, etc.).") || ""}
            />
            {!chosenItemIsExternal && !(isExternal && !order?.id) && (
              <div className="flex flex-wrap gap-5">
                <SmallCheckBox
                  defaultValue={order?.prioritized}
                  isDisabled={isDisabled || !hasAccess}
                  label={`orders.orders_basket.add_form.priority`}
                  onChange={e => onOrderChange({ ...order, prioritized: e.target.checked } as Order)}
                  helperText={t("orders.orders_basket.add_form.priority.helper", "Mark this order as high priority for faster processing.") || ""}
                />
                <SmallCheckBox
                  isDisabled={isDisabled || !hasAccess}
                  defaultValue={order?.pendingMeasures}
                  label={`orders.orders_basket.add_form.missing_measures`}
                  onChange={e => onOrderChange({ ...order, pendingMeasures: e.target.checked } as Order)}
                  helperText={t("orders.orders_basket.add_form.missing_measures.helper", "Indicate if customer measurements are still pending.") || ""}
                />
              </div>
            )}
            <div>
            <div className="flex justify-between items-center">
              <p>{`${t("orders.orders_basket.add_form.files")} [${order?.attachmentUrls?.length || 0}] (${CONFIG.MAX_FILE_UPLOAD}MB):`}</p>
              {(order?.attachmentUrls ?? []).length > 0 && (
                <ButtonV2
                  onClick={downloadAllFiles}
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  {t("global.download_all")}
                </ButtonV2>
              )}
            </div>
              <FileList
                existingFiles={order?.attachmentUrls || []}
                pendingFiles={order?.files || []}
                onRemoveExisting={removeLink}
                onRemovePending={removeFile}
                onRenamePending={renameFile}
                isDisabled={isDisabled || !hasAccess}
                resourceLinkType="private"
              />
              <FileUploadWrapper
                label={`${t("orders.orders_basket.add_form.files.button")}.`}
                onChange={processFile}
                isDisabled={isDisabled || !hasAccess}
                allowedMultipleFiles
                maxSize={50}
              />
            </div>
          </>
          )}
        </div>
        { showTracking && !(isExternal && !order?.id) && <OrderTrackingForm 
          onOrderChange={(e: string) => onOrderChange({ ...order, status: e } as Order)} 
          order={order} 
          orderId={order?.id!} 
          shopId={order?.shopIdV2!} 
          isDisabled={isDisabled} 
          />}
      </div>
      {disabledActionButton}
      {!(isExternal && !order?.id) && <PaymentPlanManager
        isOpen={showPaymentPlanManager}
        setIsOpen={setShowPaymentPlanManager}
        orderId={order?.id?.toString()}
        itemName={(order?.dress as OrderDress)?.name}
        onPaymentPlanUpdated={handlePaymentPlanUpdated}
      />}
      {showItemModal && newItem && (
        <Modal 
          modalTitle={t("sales.form.add_model") || "Add a new model"}
          canClose 
          onClose={() => {
            setShowItemModal(false);
            setNewItem(null);
          }}
        >
          <>
            <div className="space-y-5">
              <InputShortText
                label={t("admin.collections.items.form.name") || "Name"} 
                validationFn={(value) => !!value && isValidText(value, 3)} 
                onChange={e => setNewItem({ ...newItem, name: e.currentTarget.value } as Item)}
                errorMessage={"errors.too_short"}
                defaultValue={newItem?.name || ''}
                isDisabled={isDisabled || !hasAccess}
              />
              <PriceField 
                label={t("admin.collections.items.form.price") || "Price"}
                currency={newItem?.currency || 'EUR'}
                price={newItem?.price || 0}
                currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                onCurrencyChange={e => setNewItem({ ...newItem, currency: e } as Item)}
                onPriceChange={value => setNewItem({ ...newItem, price: value, currency: newItem?.currency || 'EUR' } as Item)}
                isDisabled={isDisabled || !hasAccess}
              />
            </div>
            <div className="flex justify-center mt-5">
              <PrimaryButton 
                text={t("global.create") || "Create"} 
                fn={handleSaveItem}
                actionName="Create Item"
                isDisabled={!newItem?.name || !isValidText(newItem.name, 3) || !newItem.price || newItem.price <= 0}
              />
            </div>
          </>
        </Modal>
      )}
      {showCatalogueSelectionModal && newItem && collections.length > 0 && (
        <Modal 
          modalTitle={t("sales.form.select_collection") || "Select Collection"}
          canClose 
          onClose={() => {
            setShowCatalogueSelectionModal(false);
            setNewItem(null);
            setSelectedCollectionId(null);
          }}
        >
          <>
            <div className="space-y-5">
              <SelectInput
                values={collections.map(c => ({ value: String(c.id!), readableValue: c.name }))}
                onChange={(e) => setSelectedCollectionId(Number(e.target.value))}
                label={t("sales.form.select_collection") || "Select Collection"}
                defaultValue={selectedCollectionId ? String(selectedCollectionId) : ''}
                isDisabled={isDisabled || !hasAccess}
              />
            </div>
            <div className="flex justify-center mt-5">
              <PrimaryButton 
                text={t("global.add") || "Add"} 
                fn={handleAddItemToCollection}
                actionName="Add Item to Collection"
                isDisabled={!selectedCollectionId}
              />
            </div>
          </>
        </Modal>
      )}
    </>
  );
}

export default OrderForm;
